import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ActionListItem, SeasonCounter } from '@tww/shared';
import { ACTION_STATUS_LABELS, todayIso } from '@tww/shared';
import { api } from '../../api/client';
import { ErrorState, Skeleton } from '../../ui/basics';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** Monatsraster der Saison (Plan 04 Abschnitt 5.2): Aktionen als farbige Blöcke nach Status, mehrtägig als Balken, Wochenenden markiert */
export function ActionCalendar({ seasonKey, seasons, filters = {}, q: search = '' }: { seasonKey: string; seasons: SeasonCounter[]; filters?: Record<string, string[]>; q?: string }) {
  const nav = useNavigate();
  const season = seasons.find((s) => s.key === seasonKey);
  // Frühjahr: Februar bis Mai, Sommer: Juni bis September, Herbst: Oktober bis Dezember; ohne Saison: laufender Monat ± 1
  const range = useMemo(() => {
    const now = new Date();
    if (!season) return { year: now.getFullYear(), from: Math.max(0, now.getMonth() - 1), to: Math.min(11, now.getMonth() + 1) };
    if (season.code === 'F') return { year: season.year, from: 1, to: 4 };
    if (season.code === 'S') return { year: season.year, from: 5, to: 8 };
    if (season.code === 'H') return { year: season.year, from: 9, to: 11 };
    return { year: season.year, from: 0, to: 11 };
  }, [season]);
  const [offset, setOffset] = useState(0);
  const months = useMemo(() => {
    const out: { y: number; m: number }[] = [];
    for (let m = range.from; m <= range.to; m++) {
      const d = new Date(range.year, m + offset, 1);
      out.push({ y: d.getFullYear(), m: d.getMonth() });
    }
    return out;
  }, [range, offset]);
  const from = isoOf(months[0]!.y, months[0]!.m, 1);
  const last = months[months.length - 1]!;
  const to = isoOf(last.y, last.m, new Date(last.y, last.m + 1, 0).getDate());
  // Dieselben Filter wie die Liste (ohne Saison, der Zeitraum ersetzt sie)
  const params = useMemo(() => {
    const p = new URLSearchParams({ from, to });
    if (search) p.set('q', search);
    for (const [k, v] of Object.entries(filters)) if (k !== 'season' && v.length) p.set(`filter[${k}]`, v.join(','));
    return p.toString();
  }, [from, to, search, filters]);
  const q = useQuery({ queryKey: ['actions-calendar', params], queryFn: () => api.get<{ items: ActionListItem[] }>(`/actions/calendar?${params}`).then((r) => r.items), placeholderData: (p) => p });
  const today = todayIso();
  if (q.isLoading) return <Skeleton rows={6} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const items = q.data ?? [];
  return (
    <div className="cal-wrap">
      <div className="cal-nav">
        <button type="button" className="btn sm" onClick={() => setOffset((o) => o - 1)} aria-label="Monat zurück">
          ‹
        </button>
        <span>
          {MONTHS[months[0]!.m]} {months[0]!.y} bis {MONTHS[last.m]} {last.y}
        </span>
        <button type="button" className="btn sm" onClick={() => setOffset((o) => o + 1)} aria-label="Monat vor">
          ›
        </button>
        {offset !== 0 && (
          <button type="button" className="btn sm" onClick={() => setOffset(0)}>
            Saison
          </button>
        )}
        <span className="cal-legend" aria-label="Legende">
          {(Object.keys(ACTION_STATUS_LABELS) as (keyof typeof ACTION_STATUS_LABELS)[]).map((k) => (
            <span key={k}>
              <i className={`cal-ev ${k}`} aria-hidden />
              {ACTION_STATUS_LABELS[k]}
            </span>
          ))}
        </span>
      </div>
      {months.map(({ y, m }) => {
        const first = new Date(y, m, 1);
        const days = new Date(y, m + 1, 0).getDate();
        const lead = (first.getDay() + 6) % 7; // Montag = 0
        const cells: (number | null)[] = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
        while (cells.length % 7) cells.push(null);
        return (
          <div className="panel cal-month" key={`${y}-${m}`}>
            <div className="ph">
              <h2>
                {MONTHS[m]} {y}
              </h2>
              <span className="mut small">{items.filter((a) => a.date && a.date.startsWith(`${y}-${pad(m + 1)}`)).length} Aktionen</span>
            </div>
            <div className="cal-grid">
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                <div key={d} className="cal-dow">
                  {d}
                </div>
              ))}
              {cells.map((d, i) => {
                if (!d) return <div key={i} className="cal-cell empty" />;
                const iso = isoOf(y, m, d);
                const sunday = i % 7 === 6;
                const here = items.filter((a) => a.date && a.date <= iso && (a.date_end ?? a.date) >= iso);
                return (
                  <div key={i} className={`cal-cell ${sunday ? 'we' : ''} ${iso === today ? 'today' : ''}`}>
                    <span className="d">{d}</span>
                    {here.map((a) => (
                      <button type="button" key={a.id} className={`cal-ev ${a.status} ${a.date !== iso ? 'cont' : ''}`} title={`${a.label} · ${a.status_label}`} onClick={() => nav(`/aktionen/${a.id}`)}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
